/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { List } from "semantic-ui-react";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { PpqOutcome } from "@teamprevent/common/ppq";
import { updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestion } from "@teamprevent/common/model";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";

import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";

import { ppqOutcomes } from "../../triage/models";
import { TaskType } from "../../task/model";
import { ApplicationState } from "../../applicationState";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";

import { Customer } from "../../crud/customer/model";

import { customerSelector } from "../../crud/customer/selectors";

import { AssociateOption } from "../../crud/ohCase/components/details/AssociatedManagersEditor";

import { AddNextTaskFormModel } from "../../tasks/models";

import { questionnaireKey } from "./ClinicalNotes";
import { outcomeQuestionId } from "./PpqClinicalNotes";
import { ClinicalNotesProps } from "./ClinicalNotes";
import { PpqClinicalNotesConsentSection } from "./PpqClinicalNotesConsentSection";

interface StateProps {
    tasks: TaskType[];
    customer: Customer;
}

interface DispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
}

interface OwnProps {
    onTasksChange: (tasks: AddNextTaskFormModel[]) => void;
    showErrors: boolean;
    lineManagers: AssociateOption[];
    hrRepresentatives: AssociateOption[];
    showConsentSection: boolean;
}

type AllProps = ClinicalNotesProps & OwnProps & StateProps & DispatchProps;

interface State {
    tasks: AddNextTaskFormModel[];
}

const outcomeSectionId = "Outcome";
const outcomeCommentsQuestionId = "OutcomeComments";
const fitAnswer = "Fit";

const ConsentToAdviseEmployerQuestionId = "ConsentToAdviseEmployer";

class PpqClinicalNotesSectionsUnconnected extends React.Component<AllProps, State> {
    constructor(props: AllProps) {
        super(props);
        this.state = { tasks: [] };
        this.onTasksChange = this.onTasksChange.bind(this);
        this.onOutcomeChange = this.onOutcomeChange.bind(this);
    }

    public render() {
        return (
            <>
                <AccordionQuestionnaireSection
                    title="Clinical Notes"
                    id="ClinicalNotes"
                    index={this.props.index}
                >
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "CurrentRoleDuties", questionText: "Confirm proposed job role and duties" })}
                        multiline
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "ConcernDetails", questionText: "Detail the concerns declared on the PPQ form." })}
                        multiline
                    />
                    <CheckboxQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "ConcernCurrent", questionText: "Are the concerns current?" })}
                        value="ConcernCurrent"
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "TreatmentAndIntervention", questionText: "Treatment and intervention" })}
                        multiline
                        popupContent={
                            <List bulleted>
                                <List.Item>Is the employee currently under the c/of their GP and/or specialist?</List.Item>
                                <List.Item>Medication</List.Item>
                                <List.Item>Is the treatment effective, are there any side effects?</List.Item>
                                <List.Item>Any further plans for other treatment options?</List.Item>
                            </List>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({
                            id: "SafetyCriticalWork",
                            questionText: "Is the individual going to be carrying out safety critical work (F2F or 30 minute appointment only)"
                        })}
                        multiline
                        popupContent={
                            <>
                                e.g. Working at heights, vocational driving or in confined spaces? If so, consider if the employee has:
                                <List bulleted>
                                    <List.Item>Been denied a job or driver’s licence on health grounds in the past</List.Item>
                                    <List.Item>A history of alcohol or substance abuse</List.Item>
                                    <List.Item>Declared a high alcohol intake/declared use of recreational drugs</List.Item>
                                    <List.Item>Taking medication for psychiatric problems</List.Item>
                                    <List.Item>Deafness / visual impairment</List.Item>
                                    <List.Item>History of epilepsy, stroke or unexplained episode of unconsciousness</List.Item>
                                    <List.Item>Recent diagnosis of diabetes or lack of clarity over control</List.Item>
                                    <List.Item>Suffered a loss of consciousness</List.Item>
                                    <List.Item>Suffered any side effects of any medication</List.Item>
                                </List>
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({
                            id: "WorkingWithVulnerablePeople",
                            questionText: "Is the employee going to be working with vulnerable adults/children? (F2F or 30 minute appointment only)"
                        })}
                        multiline
                        popupContent={
                            <>
                                If so, consider if:
                                <List bulleted>
                                    <List.Item>
                                        There is current or past history of mental health problems, including eating disorders, alcohol or substance abuse
                                    </List.Item>
                                    <List.Item>History of epilepsy</List.Item>
                                    <List.Item>Stroke or unexplained unconsciousness</List.Item>
                                </List>
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "WorkingWithHealthcareWorkers", questionText: "Is the employee going to be working with healthcare workers?" })}
                        multiline
                        popupContent={
                            <>
                                If so:
                                <List bulleted>
                                    <List.Item>Is there positive response to symptoms associated with TB?</List.Item>
                                    <List.Item>Is there evidence of any mental health problems, including eating disorders?</List.Item>
                                    <List.Item>Is there evidence of alcohol/substance abuse?</List.Item>
                                    <List.Item>Does the individual suffer with Diabetes (insulin dependent)?</List.Item>
                                </List>
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "OtherConditions", questionText: "Do any of the following apply?" })}
                        multiline
                        popupContent={
                            <>
                                If so, consider face-to-face assessment and/or FME if:
                                <List bulleted>
                                    <List.Item>Taking medication for psychiatric problems</List.Item>
                                    <List.Item>Cancer diagnosis and treatment currently or recently</List.Item>
                                    <List.Item>Disability whether physical, learning or intellectual</List.Item>
                                    <List.Item>Diabetes, insulin dependent, especially if working shifts/nights</List.Item>
                                    <List.Item>Deafness / visual impairment</List.Item>
                                    <List.Item>MSD – difficulties with standing, walking, driving, lifting, stair climbing or the use of the hands</List.Item>
                                    <List.Item>History of epilepsy, stroke or unexplained state of consciousness</List.Item>
                                </List>
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "CurrentFitness", questionText: "Current fitness" })}
                        multiline
                        popupContent={
                            <>
                                <List bulleted>
                                    <List.Item>Is the employee fit now?</List.Item>
                                    <List.Item>Do they have any limitations?</List.Item>
                                    <List.Item>Have they needed adjustments previously?</List.Item>
                                    <List.Item>Is there any upcoming treatment they know of?</List.Item>
                                    <List.Item>Will they need to attend appointments or follow-ups for assessment/investigation or treatment?</List.Item>
                                    <List.Item>Are there any adjustments they would like considered?</List.Item>
                                </List>
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "EqualityAct", questionText: "Equality Act 2010 (F2F or 30 minute appointment only)" })}
                        multiline
                        popupContent={
                            <>
                                Explain rationale
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: "OnwardManagement", questionText: "Onward Management" })}
                        multiline
                        popupContent={
                            <>
                                <List bulleted>
                                    <List.Item>FME: GP/Spec/Other</List.Item>
                                    <List.Item>OHA – F2F</List.Item>
                                    <List.Item>OHP</List.Item>
                                </List>
                            </>
                        }
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Outcome"
                    id="Outcome"
                    index={(this.props.index ?? 0) + 1}
                >
                    <DropdownQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: outcomeQuestionId, questionText: "Outcome" })}
                        options={ppqOutcomes}
                        mandatory
                        onAnswerChange={this.onOutcomeChange}
                        popupContent={
                            <>
                                Explain to the employee what advice including any adjustments will be contained within the Fitness certificate
                            </>
                        }
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({
                            id: ConsentToAdviseEmployerQuestionId,
                            // eslint-disable-next-line max-len
                            questionText: "Confirm consent has been gained from the employee to advise the employer about fitness to work including recommendations for adjustments if applicable"
                        })}
                        multiline
                    />
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({
                            id: "WitholdInformation",
                            questionText: "If the employee wishes certain information to be withheld please make a record of this here"
                        })}
                        multiline
                    />
                    {this.renderFormAndSubmitForFitnessLevel()}
                </AccordionQuestionnaireSection>
                {this.props.showConsentSection &&
                    <PpqClinicalNotesConsentSection
                        readonly={this.props.readonly}
                        questions={this.props.questions}
                        lineManagers={this.props.lineManagers}
                        hrRepresentatives={this.props.hrRepresentatives}
                        index={this.props.index}
                        ohCase={this.props.ohCase}
                    />
                }
            </>
        );
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions &&
            this.props.questions.find(question => question.id === id) && this.props.questions?.find(question => question.id === id)?.answer;
    }

    private onTasksChange(tasks: AddNextTaskFormModel[]) {
        this.setState({ tasks });
        this.props.onTasksChange(tasks);
    }

    private onOutcomeChange = (outcome: QuestionAnswer) => {
        const outcomeCommentsQuestion = this.props.questions?.find(q => q.id === outcomeCommentsQuestionId);
        if (outcomeCommentsQuestion) {
            this.props.updateAnswer({
                sectionId: outcomeSectionId,
                id: outcomeCommentsQuestionId,
                question: { ...outcomeCommentsQuestion, isValid: !!outcomeCommentsQuestion.answer || outcome === "Fit" }
            });
        }
        if (outcome !== PpqOutcome.temporarilyUnfit) {
            this.onTasksChange([]);
        }
    };

    private renderFormAndSubmitForFitnessLevel() {
        const fitness = this.getAnswerToQuestionWithId(outcomeQuestionId);
        switch (fitness) {
            case PpqOutcome.temporarilyUnfit:
                return (
                    <AddTasksInputs
                        onChange={this.onTasksChange}
                        ohCaseId={this.props.ohCase?.id || ""}
                        showErrors={this.props.showErrors}
                        taskTypes={this.props.tasks}
                        assignmentOptionRequired={true}
                    />
                );
            case PpqOutcome.unfit:
            case PpqOutcome.fitWithAdjustment:
            case PpqOutcome.fit:
            case PpqOutcome.temporarilyUnfitPendingFitForWork:
                return (
                    <TextQuestion
                        readonly={this.props.readonly || false}
                        questionConfig={({ id: outcomeCommentsQuestionId, questionText: "Fitness Certificate Comments" })}
                        multiline
                        mandatory={fitness !== fitAnswer}
                        extraContent={!this.props.readonly ?
                            <TextSnippetSelector
                                onSelect={selectedSnippet =>
                                    this.updateFitnessCertificatesCommentsQuestion(
                                        selectedSnippet,
                                        this.props.questions?.find(q => q.id === outcomeCommentsQuestionId)
                                    )}
                            />
                            : null
                        }
                    />
                );
            default:
                return null;
        }
    }

    private updateFitnessCertificatesCommentsQuestion(selectedSnippet: string, fitnessCertificatesCommentsQuestion: AnsweredQuestion) {
        if (fitnessCertificatesCommentsQuestion) {
            const newAnswer = (fitnessCertificatesCommentsQuestion.answer || "") + " " + selectedSnippet;
            this.props.updateAnswer({
                sectionId: fitnessCertificatesCommentsQuestion.sectionId,
                id: fitnessCertificatesCommentsQuestion.id,
                question: {
                    ...fitnessCertificatesCommentsQuestion,
                    answer: newAnswer,
                    isValid: !!newAnswer
                }
            });
        }
    }
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    tasks: state.ppqTriageTasks,
    customer: customerSelector(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey }))
});

export const PpqClinicalNotesSections = connect(mapStateToProps, mapDispatchToProps)(PpqClinicalNotesSectionsUnconnected);

